import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { FcGoogle } from "react-icons/fc";
import { FaSignInAlt, FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";

const AuthPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false, name: false });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) navigate("/home");
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isRegister) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, { displayName });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      setError("Erro na autenticação. Verifique seus dados.");
      console.error("Erro na autenticação:", error);
    }

    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      setError("Erro ao autenticar com o Google.");
      console.error("Erro no login com Google:", error);
    }
    setLoading(false);
  };

  const handleFocus = (field) => {
    setIsFocused(prev => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setIsFocused(prev => ({ ...prev, [field]: false }));
  };

  const styles = {
    page: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      backgroundImage: `url('/assets/7.jpg')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundAttachment: "fixed",
      position: "relative",
      padding: "20px",
    },
    overlay: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(211,47,47,0.3) 100%)",
      backdropFilter: "blur(2px)",
    },
    container: {
      position: "relative",
      padding: "40px 30px",
      borderRadius: "20px",
      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255,255,255,0.1)",
      textAlign: "center",
      width: "100%",
      maxWidth: "450px",
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255,255,255,0.2)",
      animation: "slideUp 0.6s ease-out",
    },
    welcome: {
      color: "#d32f2f",
      fontSize: "1.8rem",
      fontWeight: "800",
      marginBottom: "8px",
      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
      background: "linear-gradient(135deg, #d32f2f, #f44336)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    subtitle: {
      color: "#666",
      fontSize: "1rem",
      marginBottom: "30px",
      fontWeight: "400",
    },
    title: {
      color: "#333",
      fontSize: "1.4rem",
      fontWeight: "600",
      marginBottom: "25px",
      position: "relative",
    },
    titleAfter: {
      content: '""',
      position: "absolute",
      bottom: "-8px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "50px",
      height: "3px",
      background: "linear-gradient(90deg, #d32f2f, #f44336)",
      borderRadius: "2px",
    },
    inputGroup: {
      position: "relative",
      marginBottom: "20px",
    },
    input: {
      width: "100%",
      padding: "16px 16px 16px 50px",
      borderRadius: "12px",
      border: "2px solid #e0e0e0",
      fontSize: "16px",
      outline: "none",
      transition: "all 0.3s ease",
      backgroundColor: "rgba(255,255,255,0.9)",
      boxSizing: "border-box",
    },
    inputFocused: {
      borderColor: "#d32f2f",
      boxShadow: "0 0 0 3px rgba(211,47,47,0.1)",
      backgroundColor: "#fff",
    },
    inputIcon: {
      position: "absolute",
      left: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#999",
      fontSize: "18px",
      transition: "color 0.3s ease",
    },
    inputIconFocused: {
      color: "#d32f2f",
    },
    passwordToggle: {
      position: "absolute",
      right: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "none",
      border: "none",
      color: "#999",
      cursor: "pointer",
      fontSize: "16px",
      padding: "4px",
      borderRadius: "4px",
      transition: "all 0.3s ease",
    },
    passwordToggleHover: {
      color: "#d32f2f",
      backgroundColor: "rgba(211,47,47,0.1)",
    },
    button: {
      width: "100%",
      padding: "16px",
      margin: "8px 0",
      borderRadius: "12px",
      border: "none",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: "600",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
    },
    primaryButton: {
      background: "linear-gradient(135deg, #d32f2f, #f44336)",
      boxShadow: "0 4px 15px rgba(211,47,47,0.3)",
    },
    primaryButtonHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(211,47,47,0.4)",
    },
    googleButton: {
      background: "linear-gradient(135deg, #4285F4, #34A853)",
      boxShadow: "0 4px 15px rgba(66,133,244,0.3)",
    },
    googleButtonHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(66,133,244,0.4)",
    },
    buttonDisabled: {
      opacity: "0.6",
      cursor: "not-allowed",
      transform: "none !important",
    },
    divider: {
      display: "flex",
      alignItems: "center",
      margin: "25px 0",
      color: "#999",
      fontSize: "14px",
    },
    dividerLine: {
      flex: "1",
      height: "1px",
      background: "linear-gradient(90deg, transparent, #ccc, transparent)",
    },
    dividerText: {
      margin: "0 15px",
      fontWeight: "500",
    },
    error: {
      color: "#d32f2f",
      fontSize: "14px",
      marginTop: "10px",
      padding: "10px",
      backgroundColor: "rgba(211,47,47,0.1)",
      borderRadius: "8px",
      border: "1px solid rgba(211,47,47,0.2)",
      animation: "shake 0.5s ease-in-out",
    },
    link: {
      cursor: "pointer",
      marginTop: "20px",
      color: "#d32f2f",
      fontWeight: "600",
      fontSize: "15px",
      transition: "all 0.3s ease",
      textDecoration: "none",
    },
    linkHover: {
      color: "#f44336",
      textDecoration: "underline",
    },
    loadingSpinner: {
      width: "20px",
      height: "20px",
      border: "2px solid rgba(255,255,255,0.3)",
      borderTop: "2px solid #fff",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.overlay}></div>
      <div style={styles.container}>
        <h1 style={styles.welcome}>Karingana</h1>
        <p style={styles.subtitle}>Trivia Moçambicana</p>
        
        <h3 style={styles.title}>
          {isRegister ? "Criar Conta" : "Bem-vindo de volta"}
          <div style={styles.titleAfter}></div>
        </h3>
        
        {error && <p style={styles.error}>{error}</p>}

        <form onSubmit={handleEmailAuth}>
          {isRegister && (
            <div style={styles.inputGroup}>
              <FaUser 
                style={{
                  ...styles.inputIcon,
                  ...(isFocused.name && styles.inputIconFocused)
                }} 
              />
              <input
                type="text"
                placeholder="Seu nome completo"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onFocus={() => handleFocus('name')}
                onBlur={() => handleBlur('name')}
                required
                style={{
                  ...styles.input,
                  ...(isFocused.name && styles.inputFocused)
                }}
              />
            </div>
          )}
          
          <div style={styles.inputGroup}>
            <FaEnvelope 
              style={{
                ...styles.inputIcon,
                ...(isFocused.email && styles.inputIconFocused)
              }} 
            />
            <input
              type="email"
              placeholder="Seu email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => handleFocus('email')}
              onBlur={() => handleBlur('email')}
              required
              style={{
                ...styles.input,
                ...(isFocused.email && styles.inputFocused)
              }}
            />
          </div>
          
          <div style={styles.inputGroup}>
            <FaLock 
              style={{
                ...styles.inputIcon,
                ...(isFocused.password && styles.inputIconFocused)
              }} 
            />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => handleFocus('password')}
              onBlur={() => handleBlur('password')}
              required
              style={{
                ...styles.input,
                ...(isFocused.password && styles.inputFocused)
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                ...styles.passwordToggle,
                ...(showPassword && styles.passwordToggleHover)
              }}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          
          <button
            type="submit"
            style={{
              ...styles.button,
              ...styles.primaryButton,
              ...(loading && styles.buttonDisabled)
            }}
            disabled={loading}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 20px rgba(211,47,47,0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 15px rgba(211,47,47,0.3)";
              }
            }}
          >
            {loading ? (
              <div style={styles.loadingSpinner}></div>
            ) : (
              <FaSignInAlt />
            )}
            {loading ? "Processando..." : isRegister ? "Criar Conta" : "Entrar"}
          </button>
        </form>

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>ou</span>
          <div style={styles.dividerLine}></div>
        </div>

        <button
          onClick={handleGoogleLogin}
          style={{
            ...styles.button,
            ...styles.googleButton,
            ...(loading && styles.buttonDisabled)
          }}
          disabled={loading}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 20px rgba(66,133,244,0.4)";
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 15px rgba(66,133,244,0.3)";
            }
          }}
        >
          {loading ? (
            <div style={styles.loadingSpinner}></div>
          ) : (
            <FcGoogle />
          )}
          {loading ? "Processando..." : "Continuar com Google"}
        </button>

        <p 
          onClick={() => setIsRegister(!isRegister)} 
          style={styles.link}
          onMouseEnter={(e) => {
            e.target.style.color = "#f44336";
            e.target.style.textDecoration = "underline";
          }}
          onMouseLeave={(e) => {
            e.target.style.color = "#d32f2f";
            e.target.style.textDecoration = "none";
          }}
        >
          {isRegister ? "Já tem uma conta? Entre aqui" : "Não tem conta? Registre-se gratuitamente"}
        </p>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthPage;
